import { useEffect, useRef, useState } from "react";

// Calculates Euclidean distance between two colors in RGB
function colorDistance(a: number[], b: number[]) {
  return Math.sqrt(
    (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2
  );
}

function getDominantColorsFromImageUrl(
  url: string,
  count: number = 5
): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 64;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("No ctx");
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      const colorMap: Record<string, number> = {};
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i],
          g = data[i + 1],
          b = data[i + 2];
        const key = `${Math.round(r / 24) * 24},${Math.round(g / 24) * 24},${
          Math.round(b / 24) * 24
        }`;
        colorMap[key] = (colorMap[key] || 0) + 1;
      }
      // Get array of colors and their frequencies
      const colorEntries = Object.entries(colorMap)
        .map(([rgb, freq]) => ({
          rgb,
          freq: freq as number,
          arr: rgb.split(",").map(Number) as [number, number, number],
        }))
        .sort((a, b) => b.freq - a.freq);

      if (colorEntries.length === 0) {
        resolve([]);
        return;
      }

      // Select the first color — the most frequent
      const selected: typeof colorEntries = [colorEntries[0]];
      const used = new Set([colorEntries[0].rgb]);

      // Greedily select the next color that is maximally distant from already selected
      while (selected.length < count && selected.length < colorEntries.length) {
        let maxDist = -1;
        let best: (typeof colorEntries)[0] | null = null;
        for (const entry of colorEntries) {
          if (used.has(entry.rgb)) continue;
          // Minimum distance to any already selected color
          const minDist = Math.min(
            ...selected.map((sel) => colorDistance(entry.arr, sel.arr))
          );
          if (minDist > maxDist) {
            maxDist = minDist;
            best = entry;
          }
        }
        if (best) {
          selected.push(best);
          used.add(best.rgb);
        } else {
          break;
        }
      }

      // Convert to hex
      const sorted = selected.map((entry) => {
        const [r, g, b] = entry.arr;
        // Clamp and pad each channel to ensure valid hex
        const toHex = (x: number) =>
          Math.max(0, Math.min(255, Math.round(x)))
            .toString(16)
            .padStart(2, "0")
            .toUpperCase();
        return "#" + toHex(r) + toHex(g) + toHex(b);
      });
      resolve(sorted);
    };
    img.onerror = reject;
  });
}

export function useDominantColors(imageUrl: string | null, count: number = 6) {
  const [colors, setColors] = useState<string[] | null>(null);
  const lastUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!imageUrl || lastUrl.current === imageUrl) return;
    lastUrl.current = imageUrl;
    getDominantColorsFromImageUrl(imageUrl, count)
      .then(setColors)
      .catch(() => setColors(null));
  }, [imageUrl, count]);

  return colors;
}
